import { useMemo } from 'react'
import { useParams } from 'react-router'

import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { useVatRates } from '@features/vatRates/contexts/vatRatesContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { InvoiceEditorUrlParams } from '../../../../types/invoiceEditorUrlParams'
import { useInvoiceEditorQueryParams } from '../../contexts/invoiceEditorQueryParamsContext'
import { useFetchDefaultContact } from '../useFetchDefaultContact'
import { usePredefinedInvoiceValues } from '../usePredefinedInvoiceValues'

interface UseGetEditorValuesCommonDataOptions {
  enabled: boolean
}

// Shared hook providing common parts for hooks preparing editor values:
// useGetInvoiceEditorValues + useGetRecurringInvoiceEditorValues
export const useGetEditorValuesCommonData = ({ enabled }: UseGetEditorValuesCommonDataOptions) => {
  const { documentId } = useParams<InvoiceEditorUrlParams>()
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { queryParams } = useInvoiceEditorQueryParams()
  const { predefinedInvoiceValues, isLoading: isLoadingPredefinedInvoiceValues } = usePredefinedInvoiceValues()
  const { isLoading: isLoadingVatRates } = useVatRates()
  const { isActive: isStripeCardPaymentActive, isLoading: isStripeStatusLoading } = usePayNowStatus()

  const contactId = queryParams?.contact_id
  const shouldLoadDefaultContact = enabled && !!contactId && !documentId

  const { contact: defaultContact, isLoading: isLoadingDefaultContact } = useFetchDefaultContact({
    contactId: shouldLoadDefaultContact ? contactId : undefined,
    enabled: shouldLoadDefaultContact,
  })

  const isLoading =
    isLoadingVatRates ||
    isLoadingPredefinedInvoiceValues ||
    isLoadingDefaultContact ||
    isLoadingOrganization ||
    isStripeStatusLoading

  return useMemo(
    () => ({
      defaultContact,
      documentId,
      isLoading,
      isStripeCardPaymentActive,
      organization,
      predefinedInvoiceValues,
      shouldLoadDefaultContact,
    }),
    [
      defaultContact,
      documentId,
      isLoading,
      isStripeCardPaymentActive,
      organization,
      predefinedInvoiceValues,
      shouldLoadDefaultContact,
    ],
  )
}
